import { EllipsisHorizontalIcon } from '@heroicons/react/20/solid';
import * as React from 'react';

import { MarkdownText } from '@/components/common/MarkdownText';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TaskActivity } from '@/services/api/generated/taskActivityService';
import type { TaskUpdate } from '@/services/types/client-intake-types';
import { getActorDisplayName } from '@/utils/actorFormatting';

import { TaskUpdateEditor } from '../TaskUpdateEditor';
import { formatUpdateDate } from '../taskUpdateUtils';
import { getFieldChangeConfig } from './timelineConfig';

interface CorrelatedGroupProps {
  activityItem: TaskActivity;
  update: TaskUpdate;
  onEdit: (update: TaskUpdate) => void;
  isEditing: boolean;
  onCancelEdit: () => void;
  onSaveEdit: (content: string) => Promise<void>;
}

/**
 * Renders a field change event with its linked reason comment as a single unit.
 *
 * When a user changes a task field (status, due date, assignee, priority) and
 * provides a reason, the system creates both:
 * 1. A domain event (task_status_changed, etc.) with the change details
 * 2. A TaskUpdate comment with the reason text
 *
 * Both share the same correlation_id, allowing this component to display them
 * together as a cohesive "change + reason" block.
 *
 * Visual structure:
 * ┌─────────────────────────────────────────────────────────────┐
 * │ Actor · Jan 9, 5:34 PM                                      │
 * │ Status Changed — from Pending to Blocked — reason text      │
 * └─────────────────────────────────────────────────────────────┘
 *
 * @see TimelineItemEvent - for standalone field changes (no reason)
 * @see TimelineItemComment - for standalone comments (not linked to events)
 */
export const TimelineItemCorrelated: React.FC<CorrelatedGroupProps> = ({
  activityItem,
  update,
  onEdit,
  isEditing,
  onCancelEdit,
  onSaveEdit,
}) => {
  const { event } = activityItem;
  const timestamp = event.occurred_at;
  const actor = event.user;
  const { agent } = event;

  // Get config for this event type
  const config = getFieldChangeConfig(event.event_code);
  const title = config?.title ?? 'Field Changed';

  // Format actor name and time
  const actorName = getActorDisplayName(actor, agent);
  const formattedTime = timestamp ? formatUpdateDate(timestamp) : '';

  // Get formatted values from config
  const rawPayload = event.payload as { old?: unknown; new?: unknown };
  const oldValue = config?.formatOldValue(rawPayload) ?? 'Unknown';
  const newValue = config?.formatNewValue(rawPayload) ?? 'Unknown';

  return (
    <div
      className={`group border-b border-slate-100 py-2 px-2 hover:bg-slate-50 dark:border-zinc-800 dark:hover:bg-zinc-800/50 transition-colors ${
        isEditing ? 'relative z-10' : ''
      }`}
    >
      {/* Author and timestamp */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-900 dark:text-zinc-100">
            {actorName}
          </span>
          <span className="text-slate-400 dark:text-zinc-500">·</span>
          <span className="text-xs text-slate-400 dark:text-zinc-500">
            {formattedTime}
          </span>
        </div>

        {/* Actions Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1 rounded-md text-slate-300 hover:text-slate-600 hover:bg-slate-100 dark:text-zinc-600 dark:hover:text-zinc-300 dark:hover:bg-zinc-700 opacity-0 group-hover:opacity-100 transition-all"
            >
              <EllipsisHorizontalIcon className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-32 z-50">
            <DropdownMenuItem
              onClick={() => onEdit(update)}
              className="text-xs"
            >
              Edit
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Content: Title and change details */}
      <p className="mt-1 text-xs font-medium text-slate-600 dark:text-zinc-300">
        {title}:{' '}
        <span className="text-slate-400 dark:text-zinc-500">{oldValue}</span>
        {' → '}
        <span className="font-medium text-slate-700 dark:text-zinc-200">
          {newValue}
        </span>
      </p>

      {/* Reason (if provided) */}
      {update.update &&
        (isEditing ? (
          <div className="mt-1.5 pl-2 border-l border-slate-200 dark:border-zinc-700">
            <span className="text-sm font-medium text-slate-500 dark:text-zinc-400">
              Reason:
            </span>
            <div className="mt-1">
              <TaskUpdateEditor
                initialContent={update.update}
                onSave={onSaveEdit}
                onCancel={onCancelEdit}
              />
            </div>
          </div>
        ) : (
          <p className="mt-1.5 text-sm text-slate-500 dark:text-zinc-400 italic pl-2 border-l border-slate-200 dark:border-zinc-700">
            <span className="not-italic font-medium text-slate-500 dark:text-zinc-400">
              Reason:{' '}
            </span>
            <MarkdownText content={update.update} className="inline" />
          </p>
        ))}
    </div>
  );
};
